/**
 * The `<print>` element: where the page is cut, and how the piece numbers its bars.
 *
 * Both are instructions to whoever prints the file rather than things written in the
 * music, which is why they share one element and why neither is a property of a note.
 *
 * `<measure-numbering>` is in force from the bar it stands in until another one replaces
 * it, so the score's single setting is written once, in the first bar of each part. The
 * format has three words for it and no count of bars, so numbering every so many bars
 * goes out as `measure` -- every bar, which is the nearest thing a reader can draw -- and
 * comes back as every bar. The count itself survives in the document's own file.
 */

import type { GlobalMeasure, Score } from "../document/types";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";

/** The `<print>` a bar carries, or nothing when it has neither to say. */
export function printNode(score: Score, index: number, global: GlobalMeasure): XmlNode | null {
  const numbering = index === 0 ? leaf("measure-numbering", numberingWord(score)) : null;
  if (global.breakBefore === null && numbering === null) return null;
  return el(
    "print",
    {
      "new-system": global.breakBefore === "system" ? "yes" : null,
      "new-page": global.breakBefore === "page" ? "yes" : null,
    },
    numbering === null ? [] : [numbering],
  );
}

function numberingWord(score: Score): string {
  const { barNumbers } = score.layout;
  if (barNumbers === "none") return "none";
  if (barNumbers === "system") return "system";
  return "measure";
}
